/*
 * Interactive map for the store locator, using the Google Maps JavaScript API.
 *
 * This is the one surface in the project that costs money per view. Store pages
 * use the Maps Embed API instead, which Google provides free with unlimited
 * usage — but no Embed mode can plot a hundred pins and re-fit to search
 * results, so the locator needs the billed API and the store pages do not.
 *
 * Two consequences shape this file:
 *
 * 1. NOTHING LOADS UNTIL THE MAP IS SEEN. A map load is billed when the map is
 *    created, so an IntersectionObserver defers both the API script and the map
 *    itself until the container scrolls into view. Someone who lands on the
 *    locator, types a postal code and clicks through to a store page from the
 *    results never scrolls to the map and is never billed for it.
 *
 * 2. IT IS PURELY ADDITIVE. The server-rendered store list and the client-side
 *    search both work with this file absent, blocked, or failing to load. The
 *    map listens for the locator's results event; the locator never waits for
 *    the map. If Google is down or the key is wrong, the page is exactly the
 *    page it was before the map existed.
 *
 * Data comes from the same directory the search uses, resolved once by
 * snippets/store-directory-src.liquid and loaded through store-search.js, so
 * pins and cards cannot disagree about where a store is.
 */
class StoreLocatorMap extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('[data-map-canvas]');
    this.apiKey = this.dataset.apiKey || '';
    // Advanced markers refuse to render without a map ID. DEMO_MAP_ID is
    // Google's development placeholder and is not fit for production, but
    // falling back to it means a missing setting produces a working map with a
    // watermark rather than a blank grey box and a console error.
    this.mapId = this.dataset.mapId || 'DEMO_MAP_ID';
    this.markers = [];

    if (!this.apiKey || !this.canvas) return;

    // Results can arrive before the map is built — the observer may not have
    // fired yet. Hold the latest set and apply it whenever the map is ready.
    this.pending = null;
    document.addEventListener('locator:results', event => {
      this.pending = event.detail;
      if (this.map) this.applyResults(this.pending);
    });

    this.observe();
  }

  /* Load only once the map is actually about to be seen. 200px of margin means
     it is ready by the time it is scrolled to, without loading for someone who
     never gets there. */
  observe() {
    if (!('IntersectionObserver' in window)) {
      this.boot();
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        observer.disconnect();
        this.boot();
      },
      { rootMargin: '200px' }
    );
    observer.observe(this);
  }

  async boot() {
    if (this.booted) return;
    this.booted = true;
    try {
      await StoreLocatorMap.loadApi(this.apiKey);
      await this.build();
    } catch (err) {
      // A map that fails is not a page that fails. Say so once, quietly, and
      // leave the list and the search exactly as they were.
      console.error('store-locator-map: could not load Google Maps', err);
      this.hidden = true;
    }
  }

  /*
   * One shared promise for the API script, so two elements on a page — or a
   * re-render from the Section Rendering API — cannot load it twice. Google's
   * script throws loudly if included more than once.
   */
  static loadApi(key) {
    if (StoreLocatorMap.apiPromise) return StoreLocatorMap.apiPromise;

    StoreLocatorMap.apiPromise = new Promise((resolve, reject) => {
      const callback = '__tandyMapsReady';
      window[callback] = () => {
        delete window[callback];
        resolve();
      };
      const script = document.createElement('script');
      const params = new URLSearchParams({
        key: key,
        libraries: 'maps,marker',
        callback: callback,
        loading: 'async',
        v: 'weekly',
      });
      script.src = 'https://maps.googleapis.com/maps/api/js?' + params;
      script.async = true;
      script.onerror = () => reject(new Error('Maps script failed to load'));
      document.head.appendChild(script);
    });
    return StoreLocatorMap.apiPromise;
  }

  async build() {
    const { Map, InfoWindow } = await google.maps.importLibrary('maps');

    this.map = new Map(this.canvas, {
      mapId: this.mapId,
      // Roughly the middle of the continental US at a zoom that shows both
      // coasts; immediately overridden by fitBounds once pins are placed.
      center: { lat: 39.5, lng: -98.35 },
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });
    this.info = new InfoWindow();

    const stores = await window.TandyStoreSearch.loadDirectory(this.dataset.directorySrc);
    this.all = stores;
    this.applyResults(this.pending || { stores: stores, fit: false });
  }

  /*
   * `detail.stores` is whatever the locator is currently showing — every store
   * on load, the search results after a search. Pins are rebuilt rather than
   * filtered so the map can never show a store the list no longer does.
   */
  async applyResults(detail) {
    const stores = (detail && detail.stores) || this.all || [];
    const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary('marker');

    this.markers.forEach(marker => { marker.map = null; });
    this.markers = [];
    this.info.close();

    const bounds = new google.maps.LatLngBounds();
    let plotted = 0;

    stores.forEach(store => {
      const lat = Number(store.lat);
      const lng = Number(store.long);
      // A store with no coordinates cannot be pinned. Silently skipping it is
      // right — it is still in the list beside the map, which is where the
      // address and phone live anyway.
      if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return;

      const pin = new PinElement({
        background: '#C52A39',
        borderColor: '#8f1f2a',
        glyphColor: '#ffffff',
      });
      const marker = new AdvancedMarkerElement({
        map: this.map,
        position: { lat: lat, lng: lng },
        title: window.TandyStoreSearch.placeName(store.location_name),
        content: pin.element,
      });
      // `gmp-click` is the current event for advanced markers; `click` was the
      // name before v3.56 and some channels still emit it. Both are bound
      // because a pin that does nothing when tapped reads as a broken map, and
      // a double fire only re-opens the same bubble.
      marker.addListener('gmp-click', () => this.openInfo(marker, store));
      marker.addListener('click', () => this.openInfo(marker, store));
      this.markers.push(marker);
      bounds.extend({ lat: lat, lng: lng });
      plotted += 1;
    });

    if (!plotted) return;
    if (plotted === 1) {
      this.map.setCenter(bounds.getCenter());
      this.map.setZoom(14);
    } else {
      this.map.fitBounds(bounds, 48);
    }
  }

  /*
   * Built as DOM rather than an HTML string: store names and addresses are
   * merchant-entered and reach here through a generated file, so string
   * concatenation would be an injection route for anyone who can edit a
   * Shopify location name.
   */
  openInfo(marker, store) {
    const wrap = document.createElement('div');
    wrap.className = 'store-locator__info';

    const name = document.createElement('strong');
    // Display name ("Denver, CO"), as on the cards; see placeName in store-search.js.
    name.textContent = window.TandyStoreSearch.placeName(store.location_name);
    wrap.appendChild(name);

    if (store.address) {
      const address = document.createElement('div');
      address.textContent = store.address;
      wrap.appendChild(address);
    }
    if (store.phone) {
      const phone = document.createElement('a');
      phone.href = 'tel:' + String(store.phone).replace(/[^\d+]/g, '');
      phone.textContent = store.phone;
      wrap.appendChild(phone);
    }
    if (store.website) {
      const link = document.createElement('a');
      link.className = 'store-locator__info-link';
      link.href = store.website;
      link.textContent = this.dataset.pageLabel || 'View store page';
      wrap.appendChild(link);
    }

    this.info.setContent(wrap);
    this.info.open({ map: this.map, anchor: marker });
  }
}

!customElements.get('store-locator-map') && customElements.define('store-locator-map', StoreLocatorMap);
